/**
 * Toast Notification Component
 *
 * A bottom-of-screen notification bar that fades quickly.
 * Replaces Ant Design's default message component for a cleaner look.
 */

import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { CheckCircleFilled, CloseCircleFilled, InfoCircleFilled, WarningFilled } from '@ant-design/icons';
import './Toast.css';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastMessage {
  id: number;
  type: ToastType;
  content: string;
  exiting?: boolean;
}

interface ToastContextType {
  success: (content: string) => void;
  error: (content: string) => void;
  info: (content: string) => void;
  warning: (content: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const TOAST_DURATION = 2500; // 2.5 seconds before fade
const FADE_DURATION = 300; // 300ms fade animation

const icons: Record<ToastType, ReactNode> = {
  success: <CheckCircleFilled />,
  error: <CloseCircleFilled />,
  info: <InfoCircleFilled />,
  warning: <WarningFilled />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const idCounter = useRef(0);

  const removeToast = useCallback((id: number) => {
    // Start exit animation
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    // Remove after animation
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, FADE_DURATION);
  }, []);

  const showToast = useCallback(
    (type: ToastType, content: string) => {
      const id = ++idCounter.current;
      setToasts((prev) => [...prev, { id, type, content }]);

      // Auto-remove after duration
      setTimeout(() => removeToast(id), TOAST_DURATION);
    },
    [removeToast]
  );

  const toast: ToastContextType = {
    success: (content) => showToast('success', content),
    error: (content) => showToast('error', content),
    info: (content) => showToast('info', content),
    warning: (content) => showToast('warning', content),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type} ${t.exiting ? 'toast-exit' : ''}`}>
            <span className="toast-icon">{icons[t.type]}</span>
            <span className="toast-content">{t.content}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

// Singleton for use outside React components (like API interceptors)
let globalToast: ToastContextType | null = null;

export function setGlobalToast(toast: ToastContextType) {
  globalToast = toast;
}

export const toast = {
  success: (content: string) => globalToast?.success(content),
  error: (content: string) => globalToast?.error(content),
  info: (content: string) => globalToast?.info(content),
  warning: (content: string) => globalToast?.warning(content),
};
